import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cx } from '../cx'
import styles from './Tooltip.module.css'

export type TooltipPlacement = 'top' | 'bottom'

export interface TooltipProps {
  content: ReactNode
  /**
   * The trigger. For role="description", pass a function and spread its argument onto the
   * focusable trigger so screen readers hear the tooltip text as a description.
   */
  children: ReactNode | ((trigger: { 'aria-describedby': string | undefined }) => ReactNode)
  placement?: TooltipPlacement
  /**
   * 'label': the tooltip repeats the trigger's accessible name, so it is hidden from
   * assistive tech. 'description': extra text, linked with aria-describedby.
   */
  role?: 'label' | 'description'
  className?: string
}

/**
 * A small label shown on hover and keyboard focus. Meets WCAG 1.4.13: it stays while the
 * pointer moves onto it, and Esc dismisses it. Never the only place instructions live.
 */
export function Tooltip({
  content,
  children,
  placement = 'top',
  role = 'description',
  className,
}: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const show = () => {
    clearTimeout(hideTimer.current)
    setOpen(true)
  }
  // A short grace period lets the pointer travel from the trigger onto the tooltip.
  const hideSoon = () => {
    clearTimeout(hideTimer.current)
    hideTimer.current = setTimeout(() => setOpen(false), 120)
  }

  useEffect(() => () => clearTimeout(hideTimer.current), [])

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation()
      setOpen(false)
    }
  }

  return (
    <span
      className={cx(styles.anchor, className)}
      onPointerEnter={show}
      onPointerLeave={hideSoon}
      onFocus={show}
      onBlur={() => setOpen(false)}
      onKeyDown={onKeyDown}
    >
      {typeof children === 'function'
        ? children({ 'aria-describedby': role === 'description' ? id : undefined })
        : children}
      {/* Always in the DOM (hidden when closed) so aria-describedby resolves before focus lands. */}
      <span
        id={id}
        role={role === 'description' ? 'tooltip' : undefined}
        aria-hidden={role === 'label' ? true : undefined}
        hidden={!open}
        className={cx(styles.tip, styles[placement])}
      >
        {content}
      </span>
    </span>
  )
}
